import React from "react";

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-white border border-[#EAEAEA] rounded-2xl ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ eyebrow, title, action }) {
  return (
    <div className="flex items-start justify-between gap-4 px-6 pt-6">
      <div>
        {eyebrow && (
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{eyebrow}</div>
        )}
        {title && (
          <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">{title}</h2>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function EmptyState({ title, hint, testId }) {
  return (
    <div className="text-center py-14 px-6" data-testid={testId || "hq-empty-state"}>
      <div className="mx-auto w-12 h-12 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] grid place-items-center">
        <span className="font-black text-lg text-[#FF6A00]">🍊</span>
      </div>
      <div className="mt-4 text-[11px] tracking-[0.32em] uppercase font-bold text-[#0A0A0A]">{title}</div>
      {hint && <p className="mt-2 text-sm text-[#4A4A4A] max-w-md mx-auto">{hint}</p>}
    </div>
  );
}

export function Skeleton({ h = 20, w = "100%", className = "" }) {
  return (
    <div
      className={`rounded-md bg-[#EAEAEA]/70 animate-pulse ${className}`}
      style={{ height: h, width: w }}
    />
  );
}

const STAGE_COLORS = {
  new:        { bg: "#FFF7EE", fg: "#C1350B", bd: "#FCE1C7" },
  contacted:  { bg: "#F0F7FF", fg: "#2E7CF6", bd: "#D6E7FF" },
  qualified:  { bg: "#F1F8FF", fg: "#2E7CF6", bd: "#D6E7FF" },
  discovery:  { bg: "#FFF3E6", fg: "#8A4C00", bd: "#FCE1C7" },
  quoted:     { bg: "#FFF9E6", fg: "#8A6D00", bd: "#F5E4B0" },
  won:        { bg: "#EBFBF2", fg: "#0FA958", bd: "#B7EBCF" },
  lost:       { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
};
const PRIORITY_COLORS = {
  low:      { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
  normal:   { bg: "#FFF7EE", fg: "#8A4C00", bd: "#FCE1C7" },
  high:     { bg: "#FFE9DA", fg: "#C1350B", bd: "#FCE1C7" },
  urgent:   { bg: "#0A0A0A", fg: "#FF6A00", bd: "#0A0A0A" },
};

export function StageBadge({ value }) {
  const c = STAGE_COLORS[value] || STAGE_COLORS.new;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
      {value || "new"}
    </span>
  );
}
export function PriorityBadge({ value }) {
  const c = PRIORITY_COLORS[value] || PRIORITY_COLORS.normal;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
      {value || "normal"}
    </span>
  );
}

export function Pagination({ page, total_pages, onPage, testIdPrefix = "hq-page" }) {
  if (total_pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-4 text-[12px]">
      <button
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        data-testid={`${testIdPrefix}-prev`}
        className="px-3 py-2 rounded-full border border-[#EAEAEA] bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F6F5F2]"
      >
        Prev
      </button>
      <div className="tracking-[0.2em] uppercase text-[#8A8A8A]">{page} / {total_pages}</div>
      <button
        disabled={page >= total_pages}
        onClick={() => onPage(page + 1)}
        data-testid={`${testIdPrefix}-next`}
        className="px-3 py-2 rounded-full border border-[#EAEAEA] bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F6F5F2]"
      >
        Next
      </button>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search…", testId = "hq-search" }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="px-4 py-2.5 rounded-full border border-[#EAEAEA] bg-white text-[13px] w-full sm:w-[280px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00] focus:border-[#FF6A00]"
    />
  );
}
